import React, { useEffect, useMemo, useRef } from "react";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import tippy from "tippy.js";

// CommentBox: rich-text editor for the list-detail comments thread.
//
// Migrated from react-quill (CVE-2025-15056 had no upstream fix) to TipTap.
// Output contract preserved verbatim:
//   * onChange receives the editor's HTML string (so the redux/API path in
//     Comments.js stays unchanged: state.comments[].comment is HTML).
//   * @-mentions render as <span class="mention" data-id="<uuid>">@Name</span>
//     so Comments.js#getMentions (regex over data-id="...") still extracts UUIDs
//     from existing rows AND from new rows, and DOMPurify-rendered legacy
//     comments keep working.

// --- Mention popover -------------------------------------------------------
//
// TipTap drives the suggestion list via @tiptap/suggestion. We render a small
// React list inside a tippy.js popover and forward keyboard events from the
// editor (ArrowUp/ArrowDown/Enter) so users can pick mentions without leaving
// the keyboard, mirroring the previous quill-mention behavior.

const MentionList = React.forwardRef(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => setSelectedIndex(0), [items]);

  const selectItem = (index) => {
    const item = items[index];
    if (item) {
      command({ id: item.id, label: item.value });
    }
  };

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelectedIndex((selectedIndex + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelectedIndex((selectedIndex + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) return null;

  return (
    <ul id="mention-list" className="mention-list">
      {items.map((item, index) => (
        <li
          key={item.id}
          className={
            "mention-item" + (index === selectedIndex ? " is-selected" : "")
          }
          onMouseDown={(e) => {
            e.preventDefault();
            selectItem(index);
          }}
        >
          {item.value}
        </li>
      ))}
    </ul>
  );
});

const buildMentionSuggestion = (getSuggestions) => ({
  items: ({ query }) => {
    const list = getSuggestions() || [];
    if (!query) return list.slice(0, 5);
    const lowered = query.toLowerCase();
    return list
      .filter((s) => s?.value?.toLowerCase().includes(lowered))
      .slice(0, 5);
  },
  render: () => {
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });
        if (!props.clientRect) return;
        popup = tippy("body", {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate: (props) => {
        component?.updateProps(props);
        if (!props.clientRect) return;
        popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
      },
      onKeyDown: (props) => {
        if (props.event.key === "Escape") {
          popup?.[0]?.hide();
          return true;
        }
        return component?.ref?.onKeyDown(props);
      },
      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
});

// --- Toolbar ---------------------------------------------------------------

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    className={"tt-btn" + (active ? " is-active" : "")}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    {children}
  </button>
);

const Toolbar = ({ editor }) => {
  if (!editor) return null;
  const headingLevels = [1, 2, 3, 4, 5, 6];

  return (
    <div className="tt-toolbar">
      <select
        className="tt-select"
        value={
          headingLevels.find((l) => editor.isActive("heading", { level: l })) ||
          ""
        }
        onChange={(e) => {
          const value = e.target.value;
          if (value === "") {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: Number(value) })
              .run();
          }
        }}
        title="Heading"
      >
        <option value="">Normal</option>
        {headingLevels.map((l) => (
          <option key={l} value={l}>{`H${l}`}</option>
        ))}
      </select>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        title="Bold"
      >
        <b>B</b>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        title="Italic"
      >
        <i>I</i>
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive("strike")}
        title="Strike"
      >
        <s>S</s>
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        active={editor.isActive({ textAlign: "left" })}
        title="Align left"
      >
        ⯇
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        active={editor.isActive({ textAlign: "center" })}
        title="Align center"
      >
        ≡
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        active={editor.isActive({ textAlign: "right" })}
        title="Align right"
      >
        ⯈
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        active={editor.isActive({ textAlign: "justify" })}
        title="Justify"
      >
        ☰
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        title="Bullet list"
      >
        •
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        title="Ordered list"
      >
        1.
      </ToolbarButton>

      <ToolbarButton
        onClick={() =>
          editor.chain().focus().sinkListItem("listItem").run()
        }
        title="Indent"
      >
        →
      </ToolbarButton>
      <ToolbarButton
        onClick={() =>
          editor.chain().focus().liftListItem("listItem").run()
        }
        title="Outdent"
      >
        ←
      </ToolbarButton>

      <input
        type="color"
        className="tt-color"
        title="Text color"
        onInput={(e) =>
          editor.chain().focus().setColor(e.target.value).run()
        }
        value={editor.getAttributes("textStyle").color || "#000000"}
      />
    </div>
  );
};

// --- Editor ----------------------------------------------------------------

const CommentBox = ({ value, setValue, suggestions }) => {
  // Keep a stable reference to suggestions so the mention extension's
  // `items()` callback always sees the latest list without recreating the
  // editor instance on every parent render.
  const suggestionsRef = useRef(suggestions);
  useEffect(() => {
    suggestionsRef.current = suggestions;
  }, [suggestions]);

  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Mention.extend({
        // Backwards-compat: parse legacy quill-mention markup so existing
        // comment rows in DynamoDB load into the editor as mention nodes
        // (not plain text). quill-mention emitted:
        //   <span class="mention" data-id="<uuid>" data-value="<name>">@Name</span>
        // without a data-type attribute, so the default Mention parseHTML
        // (which keys on data-type="mention") would miss them.
        parseHTML() {
          return [
            { tag: 'span[data-type="mention"]' },
            {
              tag: "span.mention[data-id]",
              getAttrs: (el) => ({
                id: el.getAttribute("data-id"),
                label:
                  el.getAttribute("data-label") ||
                  el.getAttribute("data-value") ||
                  el.textContent.replace(/^@/, ""),
              }),
            },
          ];
        },
      }).configure({
        // Render <span class="mention" data-id="<id>">@Name</span> so the
        // Comments.js#getMentions regex (data-id="...") and the rendered
        // markup keep matching legacy quill-mention output exactly.
        HTMLAttributes: { class: "mention" },
        renderHTML({ options, node }) {
          return [
            "span",
            {
              ...options.HTMLAttributes,
              "data-id": node.attrs.id,
              "data-label": node.attrs.label,
            },
            `@${node.attrs.label ?? node.attrs.id}`,
          ];
        },
        suggestion: buildMentionSuggestion(() => suggestionsRef.current),
      }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      // TipTap returns "<p></p>" for an empty doc; surface "" instead so
      // existing "is the comment empty?" checks downstream behave the same.
      const html = ed.getHTML();
      setValue(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g., when the parent resets the editor
  // after submit) without thrashing the cursor on every keystroke.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next === "" && current === "<p></p>") return;
    if (next !== current) {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <div className="tt-comment-box">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default React.memo(CommentBox);
